import { useEffect, useRef, useState } from "react";
import VisualizadorFoto from "./VisualizadorFoto";
import { urlDaFoto } from "./url";

/*
  Campo de foto do usuário — opcional, usado no cadastro e na edição.
*/

/* re-exportado: várias telas importam daqui desde antes de existir url.js */
export { urlDaFoto };

const FORMATOS_ACEITOS = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

/* Mesmo limite do multer no backend; barrar aqui evita o upload inútil. */
const TAMANHO_MAXIMO = 5 * 1024 * 1024;

/*
  Props:
    fotoAtual  - chave já salva no banco (null quando não há)
    arquivo    - File escolhido nesta sessão, ainda não enviado
    onEscolher - recebe o File, ou null quando o usuário limpa a escolha
    onRemover  - quando existe, mostra "Remover foto" para apagar a que está salva
    aviso      - erro de validação, controlado pelo pai
*/
/**
 * JSDoc explícito: o componente é .jsx e é consumido por um .tsx. Sem isto o
 * TypeScript infere os tipos dos valores padrão e reclama de `null | undefined`.
 *
 * @param {{
 *   fotoAtual?: string | null,
 *   arquivo?: File | null,
 *   onEscolher: (arquivo: File | null) => void,
 *   onRemover?: (() => void) | null,
 *   compacto?: boolean,
 *   nome?: string,
 * }} props
 */
function FotoUsuario({
  fotoAtual = null,
  arquivo = null,
  onEscolher,
  /* só no cadastro já existente: no modal de adicionar não há o que remover */
  onRemover = null,
  compacto = false,
  /* serve só de título da foto ampliada */
  nome = "",
}) {
  const inputRef = useRef(null);
  const [erro, setErro] = useState("");
  const [previewLocal, setPreviewLocal] = useState(null);
  const [ampliada, setAmpliada] = useState(false);

  /*
    createObjectURL precisa ser revogado, senão o blob fica na memória da aba
    enquanto ela estiver aberta — o modal é reaberto muitas vezes numa sessão.
  */
  useEffect(() => {
    if (!arquivo) {
      setPreviewLocal(null);
      return undefined;
    }

    const url = URL.createObjectURL(arquivo);
    setPreviewLocal(url);

    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  const escolher = (e) => {
    const file = e.target.files?.[0];
    setErro("");

    if (!file) {
      onEscolher(null);
      return;
    }

    if (!FORMATOS_ACEITOS.includes(file.type)) {
      setErro("Formato não aceito. Use PNG, JPEG ou WEBP.");
      e.target.value = "";
      return;
    }

    if (file.size > TAMANHO_MAXIMO) {
      setErro("A foto deve ter no máximo 5MB.");
      e.target.value = "";
      return;
    }

    onEscolher(file);
  };

  const limparEscolha = () => {
    onEscolher(null);
    setErro("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const preview = previewLocal || urlDaFoto(fotoAtual);
  const lado = compacto ? 72 : 96;

  /*
    Se a foto sumiu enquanto a ampliação estava aberta (cancelou a escolha ou
    removeu a salva), zera o estado — senão ela reabriria sozinha na próxima.
  */
  useEffect(() => {
    if (!preview) setAmpliada(false);
  }, [preview]);

  return (
    <div className="d-flex align-items-center" style={{ gap: "12px" }}>
      {/*
        Com foto vira botão, para abrir ampliada — o recorte é circular e corta
        as bordas, então ver a foto inteira antes de salvar é o que faltava.
        Sem foto continua uma div: não há o que ampliar.
      */}
      {preview ? (
        <button
          type="button"
          title="Ver foto ampliada"
          onClick={() => setAmpliada(true)}
          style={{
            width: lado,
            height: lado,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            padding: 0,
            border: "1px solid #dee2e6",
            background: "#f1f3f5",
            cursor: "pointer",
          }}
        >
          <img
            src={preview}
            alt="Foto do usuário"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </button>
      ) : (
        <div
          style={{
            width: lado,
            height: lado,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            border: "1px solid #dee2e6",
            background: "#f1f3f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="fas fa-user text-muted" style={{ fontSize: lado / 2.6 }} />
        </div>
      )}

      <div style={{ minWidth: 0 }}>
        <input
          ref={inputRef}
          type="file"
          accept={FORMATOS_ACEITOS.join(",")}
          onChange={escolher}
          style={{ display: "none" }}
        />

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => inputRef.current?.click()}
        >
          <i className="fas fa-camera mr-1" />
          {preview ? "Trocar foto" : "Escolher foto"}
        </button>

        {arquivo && (
          <button
            type="button"
            className="btn btn-link btn-sm text-muted"
            onClick={limparEscolha}
          >
            Cancelar
          </button>
        )}

        {/* só faz sentido quando já existe foto salva e nenhuma nova escolhida */}
        {!arquivo && fotoAtual && onRemover && (
          <button type="button" className="btn btn-link btn-sm text-danger" onClick={onRemover}>
            Remover foto
          </button>
        )}

        <div className="text-muted" style={{ fontSize: "12px", marginTop: "2px" }}>
          {erro ? (
            <span className="text-danger">{erro}</span>
          ) : arquivo ? (
            "Será enviada ao salvar."
          ) : (
            "Opcional · PNG, JPEG ou WEBP · até 5MB"
          )}
        </div>
      </div>

      <VisualizadorFoto
        url={ampliada ? preview : null}
        nome={nome}
        onFechar={() => setAmpliada(false)}
      />
    </div>
  );
}

export default FotoUsuario;
